import { Icon } from "@/components/ui/Icon";
import type { ReservationFormData, TripType } from "../types";
import type { ReservationUi } from "../reservationFormUi";

type TripTypeSelectorProps = {
  t: ReservationUi["step1"];
  tripType: TripType;
  onTripTypeChange: (next: TripType) => void;
  formData: ReservationFormData;
  setFormData: React.Dispatch<React.SetStateAction<ReservationFormData>>;
  customizeReturn: boolean;
  setCustomizeReturn: React.Dispatch<React.SetStateAction<boolean>>;
};

/**
 * Section « Type de trajet » : sélecteur aller / aller-retour / multi-trajets /
 * mise à disposition, + réglage de durée (disposition) + case « retour
 * différent » (aller-retour). Bloc purement présentationnel extrait verbatim de
 * ReservationForm (aucun changement de comportement).
 */
export const TripTypeSelector = ({
  t,
  tripType,
  onTripTypeChange,
  formData,
  setFormData,
  customizeReturn,
  setCustomizeReturn,
}: TripTypeSelectorProps) => {
  return (
    <section className="mb-8">
      <h2 className="mb-6 flex items-center gap-2 text-2xl font-semibold text-black">
        <Icon icon="ph:map-trifold" className="h-6 w-6 text-gold-700" />
        <span>{t.tripTypeHeading}</span>
      </h2>

      <div className="grid gap-4 md:grid-cols-3">
        <button
          type="button"
          onClick={() => onTripTypeChange("one-way")}
          className={`flex items-center gap-3 rounded-lg border p-4 transition-all ${
            tripType === "one-way"
              ? "border-gold-600 bg-gold-100"
              : "border-black/10 hover:bg-black/5"
          }`}
        >
          <Icon
            icon="ph:arrow-right"
            className={`h-5 w-5 ${
              tripType === "one-way" ? "text-gold-700" : "text-black"
            }`}
          />
          <span
            className={`font-medium ${
              tripType === "one-way" ? "text-gold-700" : "text-black"
            }`}
          >
            {t.oneWay}
          </span>
        </button>

        <button
          type="button"
          onClick={() => onTripTypeChange("round-trip")}
          className={`flex items-center gap-3 rounded-lg border p-4 transition-all ${
            tripType === "round-trip"
              ? "border-gold-600 bg-gold-100"
              : "border-black/10 hover:bg-black/5"
          }`}
        >
          <Icon
            icon="ph:arrows-left-right"
            className={`h-5 w-5 ${
              tripType === "round-trip" ? "text-gold-700" : "text-black"
            }`}
          />
          <span
            className={`font-medium ${
              tripType === "round-trip" ? "text-gold-700" : "text-black"
            }`}
          >
            {t.roundTrip}
          </span>
        </button>

        <button
          type="button"
          onClick={() => onTripTypeChange("multi-trip")}
          className={`flex items-center gap-3 rounded-lg border p-4 transition-all ${
            tripType === "multi-trip"
              ? "border-gold-600 bg-gold-100"
              : "border-black/10 hover:bg-black/5"
          }`}
        >
          <Icon
            icon="ph:path"
            className={`h-5 w-5 ${
              tripType === "multi-trip" ? "text-gold-700" : "text-black"
            }`}
          />
          <span
            className={`font-medium ${
              tripType === "multi-trip" ? "text-gold-700" : "text-black"
            }`}
          >
            {t.multiTrip}
          </span>
        </button>

        <button
          type="button"
          onClick={() => onTripTypeChange("disposition")}
          className={`flex items-center gap-3 rounded-lg border p-4 transition-all ${
            tripType === "disposition"
              ? "border-gold-600 bg-gold-100"
              : "border-black/10 hover:bg-black/5"
          }`}
        >
          <Icon
            icon="ph:clock-countdown"
            className={`h-5 w-5 ${
              tripType === "disposition" ? "text-gold-700" : "text-black"
            }`}
          />
          <span
            className={`font-medium ${
              tripType === "disposition" ? "text-gold-700" : "text-black"
            }`}
          >
            {t.disposition}
          </span>
        </button>
      </div>

      {tripType === "disposition" && (
        <div className="mt-4 rounded-lg border border-black/10 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon
                icon="ph:clock-countdown"
                className="h-5 w-5 text-gold-700"
              />
              <div className="flex flex-col">
                <span className="font-medium text-black">
                  {t.dispositionDuration}
                </span>
                <span className="text-sm text-black/60">{t.minHours}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                title={t.reduceDuration}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    dispositionDuration: Math.max(
                      3,
                      prev.dispositionDuration - 1,
                    ),
                  }))
                }
                className="flex h-8 w-8 items-center justify-center rounded-full border border-black/15 text-black transition-colors hover:bg-black/5"
              >
                <Icon icon="ph:minus" className="h-4 w-4" />
              </button>
              <span className="w-12 text-center font-medium text-black">
                {formData.dispositionDuration}h
              </span>
              <button
                type="button"
                title={t.increaseDuration}
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    dispositionDuration: Math.min(
                      24,
                      prev.dispositionDuration + 1,
                    ),
                  }))
                }
                className="flex h-8 w-8 items-center justify-center rounded-full border border-black/15 text-black transition-colors hover:bg-black/5"
              >
                <Icon icon="ph:plus" className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {tripType === "round-trip" && (
        <div className="mt-4 flex items-center gap-3 rounded-lg border border-gold-300 p-4">
          <input
            type="checkbox"
            id="customizeReturn"
            checked={customizeReturn}
            onChange={(e) => setCustomizeReturn(e.target.checked)}
            className="h-5 w-5 rounded border-gold-400 text-gold-700 focus:ring-gold-300"
          />
          <label htmlFor="customizeReturn" className="font-medium text-black">
            {t.differentReturn}
          </label>
        </div>
      )}
    </section>
  );
};
